// `FDXWriter` and `FountainWriter` (SuiteCompile/Screenplay.swift): Final Draft's FDX, one `Paragraph` per element
// (and per line of a multi-line one) typed so Final Draft applies its own layout; and the script as one Fountain file,
// a title page and every scene as written, with the binder's folders as Fountain sections (`#`), which don't print.
import { fountainTitlePage, serialize } from '@writing-suite/core';
import { xmlEscape } from '../xml.ts';
import type { CompiledManuscript } from '../types.ts';
import { screenplayScript, type Element, type Paragraph, type Run } from './script.ts';
import { titleLines } from './typst.ts';

const trimSpaces = (s: string) => s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');

function fdxType(element: Element): string {
  switch (element) {
    case 'sceneHeading': return 'Scene Heading';
    case 'character': return 'Character';
    case 'parenthetical': return 'Parenthetical';
    case 'dialogue': case 'lyric': return 'Dialogue';
    case 'transition': return 'Transition';
    default: return 'Action';
  }
}

function cleanCue(p: Paragraph, line: Run[]): Run[] {
  if (p.element !== 'character') return line;
  return line.map((r) => ({ ...r, text: trimSpaces(r.text.replaceAll('^', '')) }));
}

const SPEECH = new Set<Element>(['dialogue', 'parenthetical', 'lyric']);
function endOfSpeech(i: number, paragraphs: Paragraph[]): number {
  let j = i + 1;
  while (j < paragraphs.length && SPEECH.has(paragraphs[j].element)) j += 1;
  return j;
}
function nextSpeech(after: number, paragraphs: Paragraph[]): number | null {
  const end = endOfSpeech(after, paragraphs);
  return end < paragraphs.length && paragraphs[end].element === 'character' ? end : null;
}

function texts(runs: Run[]): string {
  if (!runs.length) return '<Text></Text>';
  return runs.map((r) => {
    const style = [r.bold ? 'Bold' : null, r.italic ? 'Italic' : null, r.underline ? 'Underline' : null].filter((s) => s !== null).join('+');
    return style === '' ? `<Text>${xmlEscape(r.text)}</Text>` : `<Text Style="${style}">${xmlEscape(r.text)}</Text>`;
  }).join('');
}

/** The FDX file as text (`FDXWriter.write`, before UTF-8). */
export function fdxText(m: CompiledManuscript): string {
  const script = screenplayScript(m);
  let body = '';
  let startsNewPage = false;
  const paragraph = (type: string, runs: Run[], alignment?: string, number?: string) => {
    let attributes = `Type="${type}"`;
    if (alignment !== undefined) attributes += ` Alignment="${alignment}"`;
    if (number !== undefined) attributes += ` Number="${xmlEscape(number)}"`;
    if (startsNewPage) { attributes += ' StartsNewPage="Yes"'; startsNewPage = false; }
    return `    <Paragraph ${attributes}>${texts(runs)}</Paragraph>\n`;
  };
  const paragraphs = script.paragraphs;
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    // Dual dialogue: this speech and the next inside one DualDialogue.
    const second = p.element === 'character' ? nextSpeech(i, paragraphs) : null;
    if (second !== null && paragraphs[second].isDual) {
      const end = nextSpeech(second, paragraphs) ?? endOfSpeech(second, paragraphs);
      let inner = '';
      for (const q of paragraphs.slice(i, end)) {
        for (const line of q.lines) inner += '      ' + trimSpaces(paragraph(fdxType(q.element), cleanCue(q, line)));
      }
      body += `    <Paragraph>\n      <DualDialogue>\n${inner}      </DualDialogue>\n    </Paragraph>\n`;
      i = end;
      continue;
    }
    switch (p.element) {
      case 'pageBreak': startsNewPage = true; break;
      case 'centered': for (const line of p.lines) body += paragraph('Action', line, 'Center'); break;
      case 'lyric': for (const line of p.lines) body += paragraph('Dialogue', line.map((r) => ({ ...r, italic: true }))); break;
      default:
        p.lines.forEach((line, n) => { body += paragraph(fdxType(p.element), cleanCue(p, line), undefined, n === 0 ? p.sceneNumber : undefined); });
    }
    i += 1;
  }
  let title = '';
  if (m.preset.render.titlePage.include) {
    const field = (key: string) => script.titlePage.find((f) => f.key === key)?.value;
    const lines: [Run[], string][] = [];
    const blank = (alignment: string): [Run[], string] => [[], alignment];
    for (let k = 0; k < 18; k++) lines.push(blank('Center'));
    lines.push(...titleLines(field('title') ?? m.title, true).map((l): [Run[], string] => [l, 'Center']));
    lines.push(blank('Center'), blank('Center'));
    const credit = field('credit');
    if (credit !== undefined) lines.push(...titleLines(credit).map((l): [Run[], string] => [l, 'Center']), blank('Center'));
    const author = field('author') ?? field('authors');
    if (author !== undefined) lines.push(...titleLines(author).map((l): [Run[], string] => [l, 'Center']));
    const contact = field('contact');
    if (contact !== undefined) {
      for (let k = 0; k < 12; k++) lines.push(blank('Left'));
      lines.push(...titleLines(contact).map((l): [Run[], string] => [l, 'Left']));
    }
    title = '  <TitlePage>\n    <Content>\n' + lines.map(([runs, alignment]) => `      <Paragraph Alignment="${alignment}">${texts(runs)}</Paragraph>\n`).join('')
      + '    </Content>\n  </TitlePage>\n';
  }
  return `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
<FinalDraft DocumentType="Script" Template="No" Version="5">
  <Content>
${body}  </Content>
${title}</FinalDraft>
`;
}

/** `FDXWriter.write`. */
export function writeFDX(m: CompiledManuscript): Uint8Array {
  return new TextEncoder().encode(fdxText(m));
}

const trimNewlines = (s: string) => s.replace(/^[\n\r\u000b\u000c\u0085\u2028\u2029]+|[\n\r\u000b\u000c\u0085\u2028\u2029]+$/g, '');

/** `FountainWriter.write`. */
export function writeFountain(m: CompiledManuscript): string {
  const script = screenplayScript(m);
  // A play's scenes are sections too ("## Scene Two"): they have no headings of their own, and that's how an import
  // tells a play from a screenplay. A sitcom's and a radio play's scenes are lettered or numbered by order: the same.
  const format = m.preset.render.screenplay?.format ?? 'screen';
  const play = format === 'stage-us' || format === 'stage-uk' || format === 'multicam' || format === 'radio';
  const parts: string[] = [];
  // A title page the writer typed at the top of the first scene has to open the file.
  const first = m.sections.find((s) => s.matter === null && !/^\p{White_Space}*$/u.test(s.source ?? ''));
  let written: { id: string | null; rest: string } | null = null;
  if (first && first.source !== null && fountainTitlePage(first.source).length) {
    const source = first.source;
    const blank = source.indexOf('\n\n');
    parts.push(blank >= 0 ? source.slice(0, blank) : source);
    written = { id: first.fragmentID, rest: blank >= 0 ? source.slice(blank + 2) : '' };
  } else if (m.preset.render.titlePage.include) {
    parts.push(script.titlePage.map((field) => {
      const key = field.key.split(' ').filter((w) => w !== '').map((w) => [...w][0].toUpperCase() + [...w].slice(1).join('')).join(' ');
      const lines = field.value.split('\n');
      return lines.length > 1 ? `${key}:\n` + lines.map((l) => `    ${l}`).join('\n') : `${key}: ${field.value}`;
    }).join('\n'));
  }
  for (const section of m.sections) {
    if (section.matter !== null) continue;
    let text = section.source ?? serialize(section.blocks);
    if (written && section.fragmentID === written.id) text = written.rest;
    text = trimNewlines(text);
    if ((section.role === 'part' || section.role === 'chapter') && section.title !== '') {
      parts.push(`${section.role === 'part' ? '#' : '##'} ${section.title}`);
    } else if (play && section.role === 'scene' && section.title !== '') {
      parts.push(`## ${section.title}`);
    }
    if (text !== '') parts.push(text);
  }
  return parts.join('\n\n') + '\n';
}
